import {
    type ComponentProps,
    cloneElement,
    type ReactElement,
    type ReactNode,
    type PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Focusable, Tooltip as RacTooltip, TooltipTrigger as RacTooltipTrigger } from 'react-aria-components';
import { useOverlayContainer } from '../../overlays/overlayContainer';
import styles from './Tooltip.module.css';

/** §4's layering table places tooltips with dropdown menus and combobox popovers. */
const TOOLTIP_Z = 'var(--et-z-menu)';

/**
 * How long a pointer must rest before the tooltip appears, and how long it lingers after leaving.
 *
 * Numbers rather than tokens, and the distinction is real: `motion/*` are the durations of
 * ANIMATIONS, and this is hover intent — how long a user must mean it. No token family covers it,
 * and inventing one for a single consumer is the wrong end of §0's rule of two. Exported for the
 * same reason `TOAST_DEFAULT_DURATION_MS` is: a caller that needs to override it should not have to
 * guess what it is overriding.
 *
 * The library's own default is 1500ms, which is far too long for §9's permission rule — the reason
 * a control is disabled is the answer to a question the user is asking *now*.
 */
export const TOOLTIP_DELAY_MS = 400;
export const TOOLTIP_CLOSE_DELAY_MS = 200;

/*
 * The PHYSICAL four, deliberately — not the logical `start`/`end` the library also accepts.
 * The library resolves whatever it is given down to a physical `data-placement` attribute,
 * and that attribute is what the stylesheet's gap rules key off. Accepting the logical pair
 * here would mean two prop values that produce one selector, which is a way for a caller to
 * write something that looks supported and lands in a rule nobody wrote.
 */
export const TOOLTIP_PLACEMENTS = ['top', 'bottom', 'left', 'right'] as const;
export type TooltipPlacement = (typeof TOOLTIP_PLACEMENTS)[number];

/**
 * What `Focusable` will accept as its child, derived from the library rather than re-stated.
 *
 * Its declared type is narrower than what it actually supports — a host element (`ReactElement<…,
 * string>`), where the library's own documented usage and this package's callers both pass a
 * COMPONENT that renders one. So the public `children` below stays honest (`ReactElement`) and the
 * narrowing happens here, at the one boundary, rather than being pushed onto every caller as a
 * restriction the runtime does not impose. Derived via `ComponentProps` so that if the library ever
 * widens or changes it, this follows without an import from a transitive `@react-types` package.
 */
type FocusableChild = ComponentProps<typeof Focusable>['children'];

/** The pointer handlers this component chains onto its child. */
interface HoverHandlers {
    onPointerEnter?: (event: ReactPointerEvent<HTMLElement>) => void;
    onPointerLeave?: (event: ReactPointerEvent<HTMLElement>) => void;
}

export interface TooltipProps {
    /**
     * The short label. §5 caps it at roughly 28ch — this is a LABEL, never the only home for
     * information the user must have, and never interactive content (that is a popover).
     */
    readonly content: ReactNode;
    /**
     * The trigger. Any single element that can take focus and DOM props — our `Button`, a native
     * control, or the `aria-disabled` span `PermissionGuard` builds.
     */
    readonly children: ReactElement;
    readonly placement?: TooltipPlacement;
    readonly delayMs?: number;
    /** Disables the tooltip itself, not the trigger — nothing is shown on hover or focus. */
    readonly disabled?: boolean;
    /**
     * Whether the tooltip is also the trigger's accessible description (`aria-describedby`). The
     * default, and the right answer whenever the tooltip says something the trigger's name does not
     * — a disabled control's reason, for instance.
     *
     * `false` when the tooltip only repeats the trigger's accessible name — the label of an
     * icon-only control whose name is already its label. Describing it as well would have a screen
     * reader announce the same words twice; the tooltip then stays visual, for sighted users.
     */
    readonly describesTrigger?: boolean;
    readonly 'data-testid'?: string | undefined;
}

/** A ref value as React 19 passes it: a callback, an object, or nothing. */
type AnyRef<T> = ((instance: T | null) => void) | { current: T | null } | null | undefined;

function assignRef<T>(ref: AnyRef<T>, value: T | null): void {
    if (typeof ref === 'function') {
        ref(value);
    } else if (ref !== null && ref !== undefined) {
        ref.current = value;
    }
}

function isEventHandlerKey(key: string): boolean {
    return /^on[A-Z]/.test(key);
}

/**
 * The trigger for a tooltip that must not describe it (`describesTrigger={false}`).
 *
 * `Focusable` clones its child with the library's trigger props — the focus and keyboard handlers,
 * `tabIndex`, the ref, and `aria-describedby` pointing at the open tooltip. This component sits
 * between the two: it receives those props, drops `aria-describedby`, and passes the rest to the
 * real trigger, merged the way `Focusable` merges them (the trigger's own value wins; handlers on
 * both sides are chained; refs on both sides both receive the element).
 */
function VisualOnlyTrigger({
    children,
    'aria-describedby': _describedBy,
    ...injected
}: { readonly children: ReactElement; readonly 'aria-describedby'?: string } & Record<string, unknown>) {
    const own = children.props as Record<string, unknown>;
    const merged: Record<string, unknown> = { ...injected };
    for (const [key, value] of Object.entries(own)) {
        const theirs = merged[key];
        if (key === 'children') {
            continue;
        }
        if (isEventHandlerKey(key) && typeof value === 'function' && typeof theirs === 'function') {
            merged[key] = (...args: unknown[]) => {
                (theirs as (...a: unknown[]) => void)(...args);
                (value as (...a: unknown[]) => void)(...args);
            };
        } else if (key === 'ref' && value !== undefined && value !== null && theirs !== undefined) {
            merged[key] = (instance: unknown) => {
                assignRef(theirs as AnyRef<unknown>, instance);
                assignRef(value as AnyRef<unknown>, instance);
            };
        } else if (value !== undefined) {
            merged[key] = value;
        }
    }
    return cloneElement(children, merged as Partial<unknown>);
}

/**
 * §5's Tooltip — the one place besides Combobox where a library is taken (FS-3 FR-C3), and behaviour is
 * all it supplies. Every pixel below comes from the §2–§4 tokens.
 *
 * **On hover AND on keyboard focus, per §5's own wording** ("never hover-only, or it is invisible
 * to keyboard users"). The two halves come from different places, and that split is the one thing
 * worth knowing about this file.
 *
 * **Focus is the library's**, and it is the half worth a dependency: the focus path, `Esc` to
 * dismiss, the `aria-describedby` wiring to the trigger, and not stealing focus into the tooltip.
 *
 * **Hover is OURS, because the library does not supply it here.** `Focusable` wires `useFocusable`
 * and nothing else — there is no hover in it — so a tooltip triggered through it opens on focus and
 * never on pointer. Confirmed in Chromium at wave D rather than inferred: the tooltip simply never
 * appeared on hover. That is §5's rule broken in the less obvious direction, and it would have
 * shipped looking correct to anyone testing with a keyboard. The library's own hover path is
 * reachable only through its `Button`, which renders the bare `disabled` attribute that FS-3 FR-C4 and
 * §9 both forbid, so routing through it is not open to us.
 *
 * So the trigger is CONTROLLED: the library opens and closes it through `onOpenChange`, and the two
 * pointer handlers below write to the same state. Hover intent is a timer and a cancel — the parts
 * that are genuinely hard stay with the library, which is FS-3 FR-C3's cap honoured rather than widened.
 *
 * **The trigger is wrapped in `Focusable` rather than required to be a library Button** for the
 * same reason: it only wires handlers onto whatever element is passed, which leaves our components
 * in charge of their own markup. It is also what lets `PermissionGuard` hand this an `aria-disabled`
 * element that is still in the tab order — the exact case §9's permission rule is written for.
 *
 * **The overlay is portalled into the nearest `Modal`** — see `overlays/overlayContainer.ts` for
 * why a tooltip that portals to the document is invisible inside a dialog.
 */
export function Tooltip({
    content,
    children,
    placement = 'top',
    delayMs = TOOLTIP_DELAY_MS,
    disabled = false,
    describesTrigger = true,
    'data-testid': testId,
}: TooltipProps) {
    const container = useOverlayContainer();
    const [open, setOpen] = useState(false);
    // Which tooltip element is current; a reopen after a scroll starts a new one (BUG-181).
    const [generation, setGeneration] = useState(0);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The trigger element, captured on pointer entry or at a focus open, so leaving the TOOLTIP can
    // apply the same focus-outranks-pointer rule as leaving the trigger does.
    const triggerElement = useRef<HTMLElement | null>(null);
    // The frames right after a focus open (BUG-181), and whether a key or pointer was pressed since.
    const focusOpenSettling = useRef(false);
    const settleFrame = useRef<number | null>(null);
    const pressedSinceOpen = useRef(false);
    const removePressListeners = useRef<(() => void) | null>(null);

    /** Whether focus is on the trigger (or inside it), as last seen on pointer entry or a focus open. */
    function triggerHasFocus(): boolean {
        return triggerElement.current?.contains(document.activeElement) === true;
    }

    /** Ends the frames after a focus open: the frame request and the press listeners go with them. */
    const endFocusSettling = useCallback(() => {
        focusOpenSettling.current = false;
        if (settleFrame.current !== null) {
            cancelAnimationFrame(settleFrame.current);
            settleFrame.current = null;
        }
        removePressListeners.current?.();
        removePressListeners.current = null;
    }, []);

    const clearHoverTimer = useCallback(() => {
        if (hoverTimer.current !== null) {
            clearTimeout(hoverTimer.current);
            hoverTimer.current = null;
        }
    }, []);

    // A pending timer that fires after unmount would set state on a gone component — and, more to
    // the point, would raise a tooltip because the pointer crossed a control on a screen the user
    // has already left, with nothing left to dismiss it.
    useEffect(() => clearHoverTimer, [clearHoverTimer]);
    useEffect(() => endFocusSettling, [endFocusSettling]);

    // Disabling closes it, and forgets that it was open. While disabled the library reports no
    // close — a blur, say — so a stale `open` would otherwise bring the tooltip back, unasked, the
    // moment it is enabled again (the rail leaving and re-entering icon mode).
    useEffect(() => {
        if (disabled) {
            clearHoverTimer();
            // A reopen pending after a focus open would otherwise set `open` again (BUG-181).
            endFocusSettling();
            setOpen(false);
        }
    }, [disabled, clearHoverTimer, endFocusSettling]);

    function handlePointerEnter(event: ReactPointerEvent<HTMLElement>) {
        // Touch has no hover: a tap fires enter and never leave, so without this the tooltip opens
        // on tap and stays up until something else happens to close it.
        if (disabled || event.pointerType === 'touch') {
            return;
        }
        triggerElement.current = event.currentTarget;
        clearHoverTimer();
        hoverTimer.current = setTimeout(() => setOpen(true), delayMs);
    }

    /** Close after the close delay, unless focus is on the trigger — when it starts or when it ends. */
    function scheduleClose() {
        clearHoverTimer();
        // Focus outranks the pointer: a keyboard user who has tabbed to this control and then
        // happens to move the mouse should not lose the reason they are in the middle of reading.
        if (triggerHasFocus()) {
            return;
        }
        hoverTimer.current = setTimeout(() => {
            hoverTimer.current = null;
            // Checked again here because focus can arrive during the delay, and nothing else
            // cancels this timer when it does: the library's focus open asks for `true` while the
            // tooltip is still open, so `onOpenChange` is never called (BUG-182).
            if (!triggerHasFocus()) {
                setOpen(false);
            }
        }, TOOLTIP_CLOSE_DELAY_MS);
    }

    function handlePointerLeave(event: ReactPointerEvent<HTMLElement>) {
        triggerElement.current = event.currentTarget;
        scheduleClose();
    }

    /*
     * The library's open and close, with a close right after a focus open followed by one reopen
     * (BUG-181). The library opens this tooltip only on focus — hover is ours, above — and closes it
     * on blur, on Escape, on a press of the trigger, and when an element holding the trigger scrolls
     * (`useCloseOnScroll`). Tabbing to a trigger below the fold makes the browser scroll it into
     * view, and the browser reports that scroll on a later frame. When the report lands after the
     * open, the library closes the reason the user just reached, with the trigger still focused
     * (seen in WebKit). The close itself is right: WebKit may place the tooltip before the scroll,
     * where the trigger was, so it must not simply be kept.
     *
     * So for two frames after a focus open, a close that leaves the trigger focused, with no key or
     * pointer pressed since the open, is followed by one reopen on the next frame, where the scroll
     * left the trigger. Frames, not a timer: a pending `scroll` event is dispatched in the next
     * frame's update, before its animation callbacks, however slow the frame. The reopen ends the
     * window, so any later close is final — a scroll the user makes closes the tooltip as the
     * library intends, even one already running — and blur, Escape and a press close it at any time.
     *
     * Presses are seen by `document` capture listeners added at the open, so a press anywhere, the
     * trigger's own included, is recorded before the next frame decides.
     */
    function startFocusSettling() {
        endFocusSettling();
        pressedSinceOpen.current = false;
        focusOpenSettling.current = true;
        const onPress = () => {
            pressedSinceOpen.current = true;
        };
        document.addEventListener('keydown', onPress, true);
        document.addEventListener('pointerdown', onPress, true);
        removePressListeners.current = () => {
            document.removeEventListener('keydown', onPress, true);
            document.removeEventListener('pointerdown', onPress, true);
        };
        settleFrame.current = requestAnimationFrame(() => {
            settleFrame.current = requestAnimationFrame(() => {
                settleFrame.current = null;
                endFocusSettling();
            });
        });
    }

    function handleOpenChange(next: boolean) {
        if (next) {
            // The library opens only on focus, so the focused element is the trigger.
            if (document.activeElement instanceof HTMLElement) {
                triggerElement.current = document.activeElement;
            }
            startFocusSettling();
            setOpen(true);
            return;
        }
        setOpen(false);
        // Inside the window, the next frame decides: by then a press or a move of focus that came
        // with this close, or after it, has been seen.
        if (focusOpenSettling.current) {
            if (settleFrame.current !== null) {
                cancelAnimationFrame(settleFrame.current);
            }
            settleFrame.current = requestAnimationFrame(() => {
                settleFrame.current = null;
                const reopen = !pressedSinceOpen.current && triggerHasFocus();
                endFocusSettling();
                if (reopen) {
                    // A new tooltip element, placed afresh: the closing one may still be on screen,
                    // exiting, and reopening it keeps the place it had before the scroll.
                    setGeneration((current) => current + 1);
                    setOpen(true);
                }
            });
            return;
        }
        endFocusSettling();
    }

    /*
     * Chained onto whatever the child already had rather than replacing it — a trigger that tracks
     * its own hover keeps doing so. `cloneElement` overwrites by default, which is the quiet way to
     * break a caller's component.
     */
    const childProps = children.props as HoverHandlers;
    const trigger = cloneElement(children, {
        onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => {
            childProps.onPointerEnter?.(event);
            handlePointerEnter(event);
        },
        onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => {
            childProps.onPointerLeave?.(event);
            handlePointerLeave(event);
        },
    } satisfies HoverHandlers as Partial<unknown>);

    return (
        <RacTooltipTrigger
            closeDelay={TOOLTIP_CLOSE_DELAY_MS}
            delay={delayMs}
            isDisabled={disabled}
            isOpen={open && !disabled}
            // Everything the library decides — focus in, blur out, Escape, an ancestor's scroll —
            // arrives here, and the pointer handlers above write to the same piece of state. One
            // source of truth for "is it up", whichever input opened it.
            onOpenChange={handleOpenChange}
        >
            {/*
             * `isDisabled` here as well as on the trigger: `Focusable` checks on mount that its child
             * can take focus unless it is disabled, and a disabled tooltip's trigger may not be able
             * to — the sidebar's nav links sit under `inert` while the drawer is closed, with their
             * tooltips off (BUG-121). Disabled, it also adds no `tabIndex` or focus handlers, which a
             * tooltip that never opens does not need; the trigger keeps its own focusability.
             */}
            <Focusable isDisabled={disabled}>
                {
                    (describesTrigger ? (
                        trigger
                    ) : (
                        <VisualOnlyTrigger>{trigger}</VisualOnlyTrigger>
                    )) as FocusableChild
                }
            </Focusable>
            <RacTooltip
                key={generation}
                /*
                 * `?? ''` on every class handed to the library, here and in Combobox — the only two
                 * places in this package that do it. A CSS-module lookup is `string | undefined`
                 * under `noUncheckedIndexedAccess`, which a DOM `className` accepts and the
                 * library's does not under `exactOptionalPropertyTypes`. Every other component
                 * assigns straight to a DOM element, so the union never surfaces there.
                 */
                className={styles.tooltip ?? ''}
                data-testid={testId}
                placement={placement}
                // The library writes `z-index: 100000` inline, off §4's scale entirely; an inline
                // style is unreachable from the stylesheet, so the token is applied here where it
                // can win. The gap between trigger and tooltip is NOT set here — it is a margin in
                // the stylesheet, so the spacing stays a token instead of becoming a pixel count in
                // this file (the same division DropdownMenu draws).
                style={{ zIndex: TOOLTIP_Z }}
                // Hoverable (WCAG 1.4.13): the pointer can move from the trigger onto the tooltip
                // without it closing. Leaving the trigger starts the close delay; arriving on the
                // tooltip within it cancels the close, and leaving the tooltip starts it again.
                onPointerEnter={clearHoverTimer}
                onPointerLeave={scheduleClose}
                /*
                 * Spread rather than passed, because omitting the key and passing `undefined` are
                 * different things under `exactOptionalPropertyTypes` and the library's prop takes
                 * only an `Element`. Outside a Modal there is no container, and the correct
                 * statement is "this prop was not supplied" rather than "it was supplied as
                 * nothing" — which is also what makes the library fall back to its own default.
                 */
                {...(container === null ? {} : { UNSTABLE_portalContainer: container })}
            >
                {content}
            </RacTooltip>
        </RacTooltipTrigger>
    );
}
